"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ReadyForADeviceUpgrade() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="221995" id="kezdje-el-a-tervezést" className="dark relative overflow-hidden bg-secondary py-24 text-secondary-foreground sm:py-32">
      <style>
        {`
          @keyframes panBackground {
            0% { background-position: 0px 0px; }
            100% { background-position: 100px 100px; }
          }
          .animate-circuit-pan {
            animation: panBackground 60s linear infinite;
          }
        `}
      </style>

      {}
      <div className="animate-circuit-pan absolute inset-0 opacity-30" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10L90 90M90 10L10 90' stroke='%23007aff' stroke-width='0.5' fill='none'/%3E%3C/svg%3E")`,
      backgroundSize: "100px 100px"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/95 via-secondary/80 to-secondary/95" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-default text-4xl font-bold tracking-tighter text-foreground sm:text-5xl lg:text-6xl">
            Készen áll a repülésre?
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground font-default">
            A következő kalandja most kezdődik. Foglaljon repülőjegyet, szállást és transzfert egyetlen felületen, a világ vezető keresőmotorjainak erejével.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-x-6">
            <Button asChild size="lg" className="w-full rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/20 hover:bg-primary/90 sm:w-auto">
              <Link to="#">
                <RefreshCw className="mr-2 h-5 w-5" />
                Kezdje el a tervezést
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full rounded-full border-border bg-background/10 text-foreground backdrop-blur-md hover:bg-accent hover:text-accent-foreground sm:w-auto">
              <Link to="#">
                Böngésszen az ajánlatok között
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
          
          <div className="mt-12 flex items-center justify-center gap-x-8 text-sm font-medium text-muted-foreground sm:gap-x-12">
             <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-primary" />
                Azonnali visszaigazolás
             </div>
             <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-primary" />
                Biztonságos fizetés
             </div>
             <div className="hidden items-center gap-2 sm:flex">
                <div className="h-2 w-2 rounded-full bg-primary" />
                24/7 Ügyfélszolgálat
             </div>
          </div>
        </div>
      </div>
    </section>;
}